import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Send, CheckCircle2, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
      toast.success("Nachricht gesendet - wir melden uns bald.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Senden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-16 lg:py-24">
      <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3">Kontakt</div>
      <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Wir freuen uns über deine Nachricht</h1>
      <p className="mt-4 text-lg text-slate-600 max-w-2xl">Fragen, Feedback oder Kooperationen? Schreib uns - wir antworten in der Regel innerhalb von 48 Stunden.</p>

      <div className="grid lg:grid-cols-5 gap-10 mt-12">
        <Card className="p-8 rounded-2xl border-slate-100 lg:col-span-3">
          {sent ? (
            <div className="text-center py-10">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
              <div className="text-xl font-semibold text-slate-900">Vielen Dank!</div>
              <p className="text-slate-600 mt-2">Deine Nachricht ist bei uns angekommen.</p>
              <Button onClick={() => setSent(false)} className="mt-6 rounded-full" variant="outline">Neue Nachricht</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1.5 h-11" data-testid="contact-name" />
              </div>
              <div>
                <Label htmlFor="email">E-Mail</Label>
                <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1.5 h-11" data-testid="contact-email" />
              </div>
              <div>
                <Label htmlFor="message">Deine Frage oder Nachricht</Label>
                <Textarea id="message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-xl mt-1.5" placeholder="Wie können wir dir helfen?" data-testid="contact-message" />
              </div>
              <Button type="submit" disabled={loading} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-6 btn-lift" data-testid="contact-submit">
                <Send className="w-4 h-4 mr-2" /> {loading ? "Wird gesendet..." : "Nachricht senden"}
              </Button>
            </form>
          )}
        </Card>

        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 rounded-2xl border-slate-100" data-testid="contact-email-card">
            <Mail className="w-6 h-6 text-emerald-700 mb-3" />
            <div className="font-semibold text-slate-900">E-Mail</div>
            <div className="text-slate-600 mt-1 text-sm">laclive@gmx.de</div>
          </Card>
          <Card className="p-6 rounded-2xl border-slate-100" data-testid="contact-response-card">
            <Clock className="w-6 h-6 text-emerald-700 mb-3" />
            <div className="font-semibold text-slate-900">Antwortzeit</div>
            <div className="text-slate-600 mt-1 text-sm">In der Regel innerhalb von 48 Stunden - Mo. bis Fr.</div>
          </Card>
        </div>
      </div>
    </div>
  );
}
